import { useEffect, useState } from 'react';
import { AppBar, Body, PageLayout } from 'components/common';

const STORAGE_KEY = 'rtcMsgLog';

type Message = {
  ts: number;
  dtRoom: number;
  dtPrev: number;
  type?: string;
  payload?: unknown;
};

type RoomSummary = {
  total: number;
  byType: Record<string, number>;
  maxBurstIn100ms: number;
  avgIntervalMs: number;
  durationMs: number;
};

type Room = {
  roomId: string;
  startTs: number;
  endTs?: number;
  messages: Message[];
  summary?: RoomSummary;
};

type LogData = { rooms: Room[] };

function readLog(): LogData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { rooms: [] };
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.rooms)) return { rooms: [] };
    return parsed as LogData;
  } catch {
    return { rooms: [] };
  }
}

function fmtTime(ts: number): string {
  return new Date(ts).toLocaleTimeString('ko-KR', { hour12: false });
}

const PageRtcMsgLog: React.FC = () => {
  const [data, setData] = useState<LogData>({ rooms: [] });
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [copyMsg, setCopyMsg] = useState<string>('');

  const refresh = (): void => setData(readLog());

  useEffect(() => {
    refresh();
  }, []);

  const handleCopy = async (): Promise<void> => {
    const json = JSON.stringify(data, null, 2);
    try {
      await navigator.clipboard.writeText(json);
      setCopyMsg('복사됨');
    } catch {
      const ta = document.createElement('textarea');
      ta.value = json;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        setCopyMsg('복사됨');
      } catch {
        setCopyMsg('복사 실패 — 아래 textarea에서 직접 복사');
      }
      document.body.removeChild(ta);
    }
    setTimeout(() => setCopyMsg(''), 2500);
  };

  const handleReset = (): void => {
    if (!window.confirm('모든 RTC 메시지 로그를 삭제할까요?')) return;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    refresh();
  };

  const totalMsgs = data.rooms.reduce((s, r) => s + (r.messages?.length ?? 0), 0);
  const maxBurstAll = data.rooms.reduce(
    (m, r) => Math.max(m, r.summary?.maxBurstIn100ms ?? 0),
    0,
  );

  return (
    <PageLayout>
      <AppBar title="RTC Msg Log" />
      <Body className="flex flex-col gap-3 px-4 py-3 pb-[30px]">
        <div className="rounded-md bg-base-bg-subtle p-3 text-sm">
          <div className="flex justify-between"><span>총 룸</span><span className="font-mono">{data.rooms.length}</span></div>
          <div className="flex justify-between"><span>총 메시지</span><span className="font-mono">{totalMsgs}</span></div>
          <div className="flex justify-between"><span>전체 max burst (100ms)</span><span className="font-mono">{maxBurstAll}</span></div>
        </div>

        <div className="flex gap-2">
          <button onClick={refresh} className="flex-1 rounded-md bg-base-bg-subtle py-2 text-sm">새로고침</button>
          <button onClick={handleCopy} className="flex-1 rounded-md bg-base-bg-strong py-2 text-sm">{copyMsg || '복사'}</button>
          <button onClick={handleReset} className="flex-1 rounded-md bg-btn-bg-alert py-2 text-sm text-btn-txt-alert">초기화</button>
        </div>

        {data.rooms.length === 0 && (
          <div className="py-8 text-center text-sm text-base-txt-secondary">
            로그 없음. (매칭 후 다시 확인)
          </div>
        )}

        {data.rooms.slice().reverse().map((room) => {
          const key = `${room.roomId}_${room.startTs}`;
          const isOpen = expanded[key];
          const s = room.summary;
          return (
            <div key={key} className="overflow-hidden rounded-md bg-base-bg-subtle">
              <button
                onClick={() => setExpanded((e) => ({ ...e, [key]: !isOpen }))}
                className="w-full px-3 py-2 text-left text-sm"
              >
                <div className="flex justify-between font-mono text-xs">
                  <span className="flex-1 truncate">{room.roomId}</span>
                  <span className="ml-2 text-base-txt-secondary">{fmtTime(room.startTs)}</span>
                </div>
                {s ? (
                  <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-base-txt-secondary">
                    <span>msgs: <span className="font-mono">{s.total}</span></span>
                    <span>burst/100ms: <span className="font-mono">{s.maxBurstIn100ms}</span></span>
                    <span>avg: <span className="font-mono">{s.avgIntervalMs}ms</span></span>
                    <span>dur: <span className="font-mono">{s.durationMs}ms</span></span>
                  </div>
                ) : (
                  <div className="mt-1 text-xs text-base-txt-secondary">진행중 ({room.messages.length} msgs)</div>
                )}
              </button>
              {isOpen && (
                <div className="max-h-64 overflow-y-auto border-t border-base-bg-strong p-2">
                  {s?.byType && (
                    <div className="mb-2 font-mono text-xs text-base-txt-secondary">
                      {Object.entries(s.byType).map(([k, v]) => `${k}:${v}`).join('  ')}
                    </div>
                  )}
                  <div className="space-y-1 font-mono text-[10px]">
                    {room.messages.map((m, i) => (
                      <div key={i} className="flex gap-2">
                        <span className="w-12 text-right text-base-txt-secondary">{m.dtRoom}ms</span>
                        <span className="w-10 text-right text-base-txt-secondary">+{m.dtPrev}</span>
                        <span className="flex-1 truncate">{m.type}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        <textarea
          readOnly
          value={JSON.stringify(data, null, 2)}
          className="mt-2 h-40 w-full rounded-md bg-base-bg-subtle p-2 font-mono text-[10px]"
          placeholder="raw JSON (직접 select all + copy 가능)"
        />
      </Body>
    </PageLayout>
  );
};

export default PageRtcMsgLog;
